import { DISPO_HALF } from './grilleReponses';

/**
 * Expressions JAMAIS coupées sur papier (2026-09-02).
 *
 * Constat utilisateur : « Remets dans l'ordre les étapes du calcul de
 * D = 48 − 8 × 10 ÷ 4. » s'imprimait « D = 48 − 8 » / « × 10 ÷ 4 » en
 * demi-colonne. Pour un élève, une expression coupée en deux est illisible.
 *
 * Règles (corpus mesuré : 7 548 énoncés, 792 formules KaTeX, 1 666
 * expressions en texte ; la plus longue fait 51 caractères visibles, la
 * pleine largeur en accepte ~70) :
 *   1. une expression (suite « opérande opérateur opérande… », ou formule
 *      KaTeX inline) est un bloc INSÉCABLE (.ep-formule) : elle descend
 *      entière à la ligne suivante si elle ne tient pas ;
 *   2. si sa largeur estimée dépasse la demi-colonne, le bloc exige la
 *      pleine largeur (getBlockDensity, cf. largeurMaxExpressionsPayload) ;
 *   3. au-delà de SEUIL_LONGUE caractères visibles (plus large que la page :
 *      aucun cas dans le corpus, règle de repli), l'expression devient un
 *      bloc de lignes coupé AU « = » (chaque « = … » sur sa ligne, aligné
 *      sous le premier « = »), à défaut APRÈS un « + » ou un « − » — jamais
 *      dans un nombre, jamais avant un opérateur, jamais entre « = » et la
 *      case d'un trou.
 * Les phrases à trous passent leurs cases sous forme de jeton (SLOT) pour
 * qu'une case reste dans le bloc de son égalité (« 6 × 1,30 = ___ »).
 */

/** Jeton d'une case de trou dans une ligne (caractère privé, non-espace). */
export const SLOT = '\uE000';
/** Largeur d'un trou sur papier : « … » et ses marges (px, cf. .ep-tt-trou). */
export const LARGEUR_SLOT = 16;
/** Au-delà de ce nombre de caractères visibles : bloc de lignes (règle 3). */
export const SEUIL_LONGUE = 60;
/** Longueur visée d'une ligne du bloc (règle 3). */
const CIBLE_LIGNE = 50;
/** Largeur moyenne d'un caractère (px) : texte 0,95 rem ; KaTeX 1,21 em. */
const PX_CAR_TEXTE = 8.4;
const PX_CAR_TEX = 9.2;

const OPS = '+−×÷=<>≤≥≠≈-';
/** Une expression en texte : au moins « opérande opérateur opérande ».
 *  Un opérande peut contenir l'espace fine de milliers (U+202F, « 1 250 ») :
 *  elle compte comme un blanc pour `\s`, et sans ce cas le nombre serait coupé
 *  hors de l'expression. Partagé avec l'écran (VueQuestion/htmlAvecKatex). */
const OPERANDE = '(?:\\S|\\u202f)+';
export const RUN_RE = new RegExp(`${OPERANDE}(?: [${OPS}] ${OPERANDE})+`, 'g');

// ─── Largeurs estimées ────────────────────────────────────────────────

const nbSlots = (s) => (s.match(new RegExp(SLOT, 'g')) || []).length;

/** Largeur (px) d'une expression en texte (les cases comptent LARGEUR_SLOT). */
export const largeurTexte = (run) => {
    const k = nbSlots(run);
    return (run.length - k) * PX_CAR_TEXTE + k * LARGEUR_SLOT;
};

/** Longueur VISIBLE approximative d'une source TeX : une fraction vaut sa
 *  ligne la plus longue, une commande de symbole vaut 1 (dont `\ldots`, le
 *  trou imprimé), les autres 0.
 *  Ce qui n'occupe rien sur le papier ne compte pas (2026-09-09) : les étais
 *  `\rule[-0.55em]{0pt}{0.55em}` des cases de fraction (22 caractères de code,
 *  0 mm imprimé), les `\phantom`, les espacements `\quad` `\;` `\,`. Comptés
 *  comme du texte, ils faisaient passer une Série flash « 42/5 = 8 + …/5 ;
 *  7/6 = 1 + …/6 » (28 caractères réels) pour 65 : coupée au deuxième « = »
 *  sur le papier (constat prof, gen_1053 ; 11 cartes 6e/5e touchées). */
export const texVisible = (tex) => {
    let s = String(tex)
        .replace(/\\rule(\[[^\]]*\])?\{[^}]*\}\{[^}]*\}/g, '')
        .replace(/\\[hv]?phantom\{[^}]*\}/g, '')
        .replace(/\\(quad|qquad|small|;|,|!|:)/g, ' ');
    for (let k = 0; k < 4; k++) {
        s = s.replace(/\\d?frac\{([^{}]*)\}\{([^{}]*)\}/g, (m, a, b) => (a.length >= b.length ? a : b));
    }
    s = s.replace(/\\(times|div|cdot|pm|le|ge|ne|approx|pi|infty|sqrt|Rightarrow|rightarrow|leftarrow|degree|circ|ldots)\b/g, 'x');
    s = s.replace(/\\[a-zA-Z]+/g, '').replace(/[{}]/g, '').replace(/\s+/g, ' ').trim();
    return s.length;
};

export const largeurTex = (tex) => texVisible(tex) * PX_CAR_TEX;

// ─── Découpe des expressions trop longues (règle 3) ───────────────────

/**
 * Découpe une expression en TEXTE en lignes : de préférence avant chaque
 * « = » (sauf le premier, qui reste sur la première ligne), sinon après un
 * « + » ou un « − » quand la ligne dépasse CIBLE_LIGNE. Retourne
 * { lignes, indent } — indent = longueur du membre avant le premier « = »
 * (pour aligner les lignes suivantes sous ce « = »).
 */
export const decouperTexte = (run) => {
    const tokens = run.split(' ');
    const lignes = [];
    let courante = [];
    let egalVus = 0;
    tokens.forEach((tok, i) => {
        const prec = tokens[i - 1];
        const estEgal = tok === '=';
        if (estEgal) egalVus += 1;
        const longueur = courante.join(' ').length;
        const coupureEgal = estEgal && egalVus >= 2;
        const coupureOp = !estEgal && prec != null && /^[+−-]$/.test(prec) && longueur + tok.length + 1 > CIBLE_LIGNE;
        if (courante.length && (coupureEgal || coupureOp)) {
            lignes.push(courante.join(' '));
            courante = [];
        }
        courante.push(tok);
    });
    if (courante.length) lignes.push(courante.join(' '));
    const premierEgal = lignes[0].indexOf(' = ');
    const indent = premierEgal > 0 ? premierEgal + 1 : 0;
    return { lignes, indent };
};

/** Positions des « = » (ou « + » / « - ») au niveau des accolades 0 d'un TeX. */
const positionsTopLevel = (tex, chars) => {
    const pos = [];
    let depth = 0;
    for (let i = 0; i < tex.length; i++) {
        const c = tex[i];
        if (c === '{') depth += 1;
        else if (c === '}') depth -= 1;
        else if (depth === 0 && chars.includes(c) && i > 0) pos.push(i);
    }
    return pos;
};

/**
 * Découpe une source TeX trop longue en lignes TeX : avant chaque « = » sauf
 * le premier ; à défaut après « + » / « - » de niveau 0, par tranches
 * d'environ CIBLE_LIGNE caractères visibles.
 */
export const decouperTex = (tex) => {
    const egaux = positionsTopLevel(tex, '=');
    let coupes = egaux.slice(1);
    if (!coupes.length) {
        const ops = positionsTopLevel(tex, '+-');
        let depuis = 0;
        coupes = [];
        ops.forEach((p) => {
            if (texVisible(tex.slice(depuis, p + 1)) > CIBLE_LIGNE) { coupes.push(p + 1); depuis = p + 1; }
        });
    }
    const lignes = [];
    let debut = 0;
    coupes.forEach((p) => { lignes.push(tex.slice(debut, p).trim()); debut = p; });
    lignes.push(tex.slice(debut).trim());
    const premierEgal = egaux.length ? texVisible(tex.slice(0, egaux[0])) + 1 : 0;
    return { lignes: lignes.filter(Boolean), indent: premierEgal };
};

// ─── Phrases à trous ──────────────────────────────────────────────────

/**
 * Lignes IMPRIMÉES d'une phrase à trous en mode classique : les « \n » du
 * générateur, puis, sur une ligne qui porte AU MOINS DEUX égalités à trous,
 * une phrase par ligne (coupure au point suivi d'une majuscule). Les cases
 * doivent déjà être représentées par SLOT (ou par {{…}} : les deux comptent).
 */
export const lignesTrous = (phrase) => String(phrase || '').split('\n').flatMap((ligne) => {
    const marquee = ligne.replace(/\{\{[^}]+\}\}/g, SLOT);
    const egalites = (marquee.match(RUN_RE) || []).filter((r) => r.includes(SLOT)).length;
    if (egalites < 2) return [ligne];
    // Pas de lookbehind (Safari < 16.4) : on coupe après le point puis on le rend.
    const morceaux = ligne.split(/\.\s+(?=[A-ZÀ-Ý«])/);
    return morceaux.map((m, i) => (i < morceaux.length - 1 ? `${m}.` : m)).filter((l) => l.trim());
});

// ─── Largeur maximale des expressions d'un payload (règle 2) ──────────

const stripHtml = (s) => String(s || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ');

/** Largeur (px) de la plus large expression d'un fragment HTML (texte + TeX). */
export const largeurMaxExpressionsHtml = (html) => {
    let max = 0;
    const s = String(html || '');
    s.replace(/\$([^$]+)\$/g, (m, tex) => { max = Math.max(max, largeurTex(tex)); return ' '; });
    const texte = stripHtml(s.replace(/\$[^$]+\$/g, ' '));
    (texte.match(RUN_RE) || []).forEach((run) => { max = Math.max(max, largeurTexte(run)); });
    return max;
};

const textesDuPayload = (payload) => {
    const c = payload?.contenu || {};
    const out = [payload?.texte];
    (c.choix || []).forEach((ch) => { if (typeof ch === 'string') out.push(ch); });
    (c.items || []).forEach((it) => { if (typeof it === 'string') out.push(it); });
    (c.pairs || []).forEach((p) => { out.push(p?.source, p?.target); });
    (c.categories || []).forEach((cat) => (cat?.items || []).forEach((it) => out.push(it)));
    if (c.subType === 'texte_trou' && c.phrase) {
        lignesTrous(c.phrase).forEach((l) => out.push(l.replace(/\{\{[^}]+\}\}/g, SLOT)));
    }
    return out.filter(Boolean);
};

/** Largeur (px) de la plus large expression insécable d'un payload. */
export const largeurMaxExpressionsPayload = (payload) =>
    textesDuPayload(payload).reduce((m, t) => Math.max(m, largeurMaxExpressionsHtml(t)), 0);

/** Une expression insécable plus large que la demi-colonne → pleine largeur. */
export const expressionsExigentPleineLargeur = (payload) =>
    largeurMaxExpressionsPayload(payload) > DISPO_HALF;
